/* global globalThis */

let _analyticsConfig = {
  platform:    'web',
  application: 'marketing_site',
  environment: 'dev',
  enabled:     false,
}

export const initAnalytics = (cfg: Partial<typeof _analyticsConfig>) => {
  _analyticsConfig = { ..._analyticsConfig, ...cfg }
}

const getPlatform    = () => _analyticsConfig.platform
const getApplication = () => _analyticsConfig.application
const getEnvironment = () => _analyticsConfig.environment

const RESERVED_EVENT_KEYS = new Set(['event', 'platform', 'application', 'environment'])

const NUMERIC_FIELDS = [
  'investment_amount', 'listed_yield', 'coupon_rate',
  'min_investment', 'yield_to_option', 'quantity', 'price'
]

const RESTRICTED_PII_KEYS = new Set([
  'email', 'emailid', 'phoneno', 'phone', 'mobile', 'mobileno',
  'fullname', 'name', 'pan', 'aadhaar', 'aadhar', 'bankaccount',
  'bankaccountnumber', 'accountno', 'accountnumber', 'ifsc',
  'demataccount', 'otp', 'dob', 'address'
])

const normalizeKey = (key: string) =>
  String(key || '').toLowerCase().replaceAll(/[^a-z0-9]/g, '')

const removeInvalidValues = (payload: Record<string, unknown> = {}) =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) =>
      value !== null && value !== undefined && value !== '' &&
      value !== 'NA' && value !== 'unknown'
    )
  )

const sanitizePayload = (payload: Record<string, unknown> = {}) =>
  Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => {
      if (RESERVED_EVENT_KEYS.has(key)) return false
      if (RESTRICTED_PII_KEYS.has(normalizeKey(key))) return false
      return removeInvalidValues({ [key]: value })[key] !== undefined
    })
  )

const getAttributionParams = () => {
  if (typeof globalThis.window === 'undefined') return {}
  try {
    let urlString = globalThis.window.location.href
    const sessionLandingUrl = sessionStorage.getItem('landingUrl')
    if (sessionLandingUrl?.trim()) urlString = sessionLandingUrl

    const parsedUrl = new URL(urlString)
    const p = parsedUrl.searchParams

    const utmSource   = p.get('utm_source')
    const utmMedium   = p.get('utm_medium')
    const utmCampaign = p.get('utm_campaign')
    const gclid       = p.get('gclid')
    const fbclid      = p.get('fbclid')
    const referrer    = (globalThis as any).document?.referrer || undefined

    let source: string
    if (gclid || p.get('gbraid') || p.get('wbraid')) {
      source = 'google'
    } else if (fbclid) {
      source = 'meta'
    } else if (p.get('msclkid')) {
      source = 'microsoft'
    } else if (utmSource) {
      source = utmSource.toLowerCase()
    } else if (referrer) {
      try { source = new URL(referrer).hostname.replace(/^www\./, '') }
      catch { source = 'referral' }
    } else {
      source = 'direct'
    }

    const attribution: Record<string, string | null | undefined> = {
      utm_source: utmSource, utm_medium: utmMedium, utm_campaign: utmCampaign,
      utm_term: p.get('utm_term'), utm_content: p.get('utm_content'),
      gclid, gbraid: p.get('gbraid'), wbraid: p.get('wbraid'),
      fbclid, msclkid: p.get('msclkid'),
      landing_page: parsedUrl.pathname, referrer,
      // eq_-prefixed: GA4 treats event params named source/medium/campaign as
      // campaign overrides, which pushed referrer hostnames into "Unassigned".
      eq_source:   source,
      eq_medium:   utmMedium,
      eq_campaign: utmCampaign,
      traffic_source: source,
    }

    return Object.fromEntries(
      Object.entries(attribution).filter(([, v]) => v !== null && v !== undefined && v !== '')
    )
  } catch (err) {
    console.error('Failed to parse attribution params:', err)
    return {}
  }
}

const JOURNEY_ID_KEY = 'analytics_journey_id'

const getJourneyId = () => {
  if (typeof localStorage === 'undefined') return undefined
  let jid = localStorage.getItem(JOURNEY_ID_KEY)
  if (!jid) {
    jid = `j_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
    localStorage.setItem(JOURNEY_ID_KEY, jid)
  }
  return jid
}

export const setAnalyticsUserId = (userId: string) => {
  if (!userId || typeof sessionStorage === 'undefined') return
  sessionStorage.setItem('analytics_user_id', String(userId))
}

export const clearAnalyticsUserId = () => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem('analytics_user_id')
  }
}

const getAnalyticsUserId = () =>
  typeof sessionStorage !== 'undefined'
    ? sessionStorage.getItem('analytics_user_id') || undefined
    : undefined

const ERROR_MESSAGE_MAX_LENGTH = 150
const PII_PATTERNS = [
  /\b[A-Z]{5}\d{4}[A-Z]\b/g,
  /\b[2-9]\d{11}\b/g,
  /\b[A-Z]{4}0[A-Z0-9]{6}\b/g,
  /\b\d{9,18}\b/g,
  /[a-zA-Z0-9._%+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  /\b(\+91|0)?[6-9]\d{9}\b/g,
]

const sanitizeErrorMessage = (msg: unknown) => {
  if (typeof msg !== 'string') return msg
  let s = msg
  for (const p of PII_PATTERNS) s = s.replace(p, '[redacted]')
  return s
}

const truncateErrorMessage = (payload: Record<string, unknown>) => {
  if (typeof payload.error_message === 'string') {
    payload.error_message = sanitizeErrorMessage(payload.error_message) as string
    if ((payload.error_message as string).length > ERROR_MESSAGE_MAX_LENGTH)
      payload.error_message = (payload.error_message as string).slice(0, ERROR_MESSAGE_MAX_LENGTH)
  }
}

const JOURNEY_STEP_MAP: Record<string, string> = {
  page_viewed:                  'acquire',
  product_viewed:               'acquire',
  search:                       'acquire',
  search_result_clicked:        'acquire',
  auth_redirect_initiated:      'acquire',
  signup_started:               'acquire',
  login_started:                'onboard',
  login_completed:              'onboard',
  onboarding_resumed:           'onboard',
  onboarding_step_viewed:       'onboard',
  onboarding_step_completed:    'onboard',
  onboarding_step_failed:       'onboard',
  fd_redirection_initiated:     'invest',
  investment_started:           'invest',
  payment_started:              'invest',
  investment_completed:         'invest',
}

const getJourneyStep = (eventName: string) => JOURNEY_STEP_MAP[eventName] || undefined

let _currentPagePath: string | undefined
let _previousPagePath: string | undefined

// Every key trackEvent has pushed on this page load. GTM's data model keeps a
// key's last value until it is overwritten (Data Layer Variable Version 1 and 2
// alike), so a key omitted from a push leaks its previous value into this
// event - e.g. search_term from a search landing on the next product_viewed.
// Each push therefore sets the keys it does not use to undefined, which clears
// them in GTM and makes the GA4 tag drop the parameter.
const _pushedKeys = new Set<string>()

export const trackEvent = (eventName: string, payload: Record<string, unknown> = {}) => {
  try {
    if (typeof globalThis.window === 'undefined') return

    if (!_analyticsConfig.enabled) {
      console.log(`[Analytics] Event '${eventName}' skipped (analytics disabled in this env)`)
      return
    }

    ;(globalThis.window as any).dataLayer = (globalThis.window as any).dataLayer || []

    const sanitizedPayload = sanitizePayload(payload)
    const currentPath = globalThis.window.location?.pathname

    if (currentPath && currentPath !== _currentPagePath) {
      _previousPagePath = _currentPagePath
      _currentPagePath = currentPath
    }

    const analyticsPayload: Record<string, unknown> = {
      event:          eventName,
      platform:       getPlatform(),
      application:    getApplication(),
      environment:    getEnvironment(),
      journey_id:     getJourneyId(),
      user_type:      payload.user_type || 'anonymous',
      user_id:        getAnalyticsUserId(),
      product:        payload.product,
      page_type:      payload.page_type,
      journey_step:   payload.journey_step || getJourneyStep(eventName),
      device_type:    /Mobi|Android|iPhone|iPad/i.test((globalThis as any).navigator?.userAgent || '')
                        ? 'mobile' : 'desktop',
      screen_width:   (globalThis.window as any).screen?.width,
      timestamp:      new Date().toISOString(),
      previous_page:  _previousPagePath,
      ...getAttributionParams(),
      ...sanitizedPayload,
    }

    const cleanPayload = removeInvalidValues(analyticsPayload)

    for (const field of NUMERIC_FIELDS) {
      if (cleanPayload[field] !== undefined) {
        const parsed = Number(cleanPayload[field])
        if (Number.isNaN(parsed)) delete cleanPayload[field]
        else cleanPayload[field] = parsed
      }
    }

    truncateErrorMessage(cleanPayload)

    // Reset keys must be added after removeInvalidValues(), which strips undefined.
    const pushPayload: Record<string, unknown> = { ...cleanPayload }
    for (const key of _pushedKeys) {
      if (!(key in cleanPayload)) pushPayload[key] = undefined
    }
    for (const key of Object.keys(cleanPayload)) _pushedKeys.add(key)

    ;(globalThis.window as any).dataLayer.push(pushPayload)
  } catch (err) {
    console.error(`Failed to push event '${eventName}' to dataLayer:`, err)
  }
}
